/**
 * Standard Onboarding API - save and retrieve onboarding step statuses
 * for non-Shopify merchants.
 *
 * Endpoints:
 *   GET  /bot/api/standard-onboarding  → retrieve step statuses
 *   POST /bot/api/standard-onboarding  → update step statuses
 */

import { getBaseUrl, buildUrl } from './utils.js';
import { getAuthHeaders } from './auth';
import { fetchInstallStatus as fetchWebchatInstallStatus } from './webchatChannel';

/**
 * Re-export of the Web Chat install-status check, used by the Standard
 * onboarding "Verify Now" step to confirm the widget script has actually
 * loaded on the merchant's website before marking activation complete.
 *
 * Returns ``{ installed, domain, installedAt }`` — installed=true means the
 * embedded widget loader posted to ``/bot/api/widget/verify-ping`` from the
 * merchant's site at least once.
 */
export const fetchStandardInstallStatus = fetchWebchatInstallStatus;

/**
 * Mapping between step IDs and API field names.
 * Maps to ONBOARDING_FIELDS: knowledgebase, settings, channel, widget, testai, verify
 */
const STEP_ID_TO_API_FIELD = {
  1: 'knowledgebase',
  2: 'settings',
  3: 'channel',
  4: 'widget',
  5: 'testai',
  6: 'verify',
};

/**
 * Default step definitions for standard onboarding.
 */
const createDefaultSteps = () => [
  { id: 1, title: 'Upload Knowledge Base',       description: 'Add your products, FAQs, policies and any other content your AI should know.',   status: 'pending', type: 'required',    action: 'Go to Knowledge Base', capabilityLine: 'Your AI will learn from your uploaded content to answer customer questions accurately.' },
  { id: 2, title: 'Configure Business Settings', description: 'Set your business type, name, timezone and language to personalise your AI.',     status: 'pending', type: 'required',    action: 'Open Settings',        capabilityLine: 'Personalised responses based on your business identity and operating hours.' },
  { id: 3, title: 'Connect a Channel',           description: 'Connect at least one channel — WhatsApp, Email, Web Chat or social media.',      status: 'pending', type: 'required',    action: 'Connect Channel',      capabilityLine: 'Your AI will be available to customers on the channels you connect.' },
  { id: 4, title: 'Customize Your Widget',       description: 'Add your brand colours, logo and welcome message to make the widget yours.',     status: 'pending', type: 'recommended', action: 'Customise Widget',     capabilityLine: 'A branded widget builds customer trust and matches your site experience.' },
  { id: 5, title: 'Test Drive Your AI',          description: 'Send a test message and see how your AI responds before going live.',            status: 'pending', type: 'required',    action: 'Test Now',             capabilityLine: 'Preview exactly how your AI assistant will respond to real customer questions.' },
  { id: 6, title: 'Verify Installation',         description: 'Run a check to confirm your widget or channel is correctly installed and live.', status: 'pending', type: 'required',    action: 'Verify Now',           capabilityLine: 'Ensures everything is working before your customers start chatting.' },
];

/**
 * Fetch all standard onboarding step statuses.
 * GET /bot/api/standard-onboarding
 *
 * @returns {Promise<Array>} array of step objects with statuses from backend
 */
export async function getStandardOnboardingSteps() {
  const base = getBaseUrl();
  const url = buildUrl(base, 'bot/api/standard-onboarding');

  let apiData = null;
  try {
    const res = await fetch(`${url}?_t=${Date.now()}`, {
      method: 'GET',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache', ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    apiData = await res.json();
    if (apiData.error) throw new Error(apiData.error);
  } catch (err) {
    console.warn('Standard onboarding API failed, using defaults:', err);
  }

  const steps = createDefaultSteps();
  if (!apiData) return steps;

  return steps.map((step) => {
    const apiField = STEP_ID_TO_API_FIELD[step.id];
    if (apiField && apiData[apiField] !== undefined) {
      const val = apiData[apiField];
      if (val === 1) return { ...step, status: 'completed' };
      if (val === 2) return { ...step, status: 'visited' };
      if (val === 3) return { ...step, status: 'skipped' };
    }
    return step;
  });
}

/**
 * Update the status of a single onboarding step.
 * POST /bot/api/standard-onboarding
 *
 * @param {number} stepId - step ID (1-6)
 * @param {string} status - 'pending' | 'visited' | 'completed' | 'skipped'
 * @returns {Promise<object|null>}
 */
export async function updateStandardOnboardingStep(stepId, status) {
  try {
    const apiField = STEP_ID_TO_API_FIELD[stepId];
    if (!apiField) {
      console.warn(`Step ${stepId} does not map to API field`);
      return null;
    }

    let apiValue = 0;
    if (status === 'completed') apiValue = 1;
    else if (status === 'visited') apiValue = 2;
    else if (status === 'skipped') apiValue = 3;

    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/standard-onboarding');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ [apiField]: apiValue }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `API returned ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    console.warn('Failed to save standard onboarding step:', err);
    return null;
  }
}
